import { Navigate, Route, Routes } from "react-router";

import { RequireAnyView, RequireAuth, RequireRole, RequireView } from "@/auth/route-guards";
import { AppLayout } from "@/components/layout/app-layout";
import { AssetsPage } from "@/features/assets/assets-page";
import { TemperatureRecordPage } from "@/features/assets/temperature-record-page";
import { ContractsIndexRedirect, ContractsPage } from "@/features/contracts/contracts-page";
import { LoanAgreementPage } from "@/features/loans/loan-agreement-page";
import { LoanAgreementsPage } from "@/features/loans/loan-agreements-page";
import { ServiceAgreementPage } from "@/features/services/service-agreement-page";
import { ServiceAgreementsPage } from "@/features/services/service-agreements-page";
import { AlertRulesPage } from "@/features/alerts/alert-rules-page";
import { ContactsPage } from "@/features/alerts/contacts-page";
import { AuditPage } from "@/features/audit/audit-page";
import { WebhookEventsPage } from "@/features/telemetry/webhook-events-page";
import { NetworkSyncPage } from "@/features/network/network-sync-page";
import { ForgotPasswordPage } from "@/features/auth/forgot-password-page";
import { LoginPage } from "@/features/auth/login-page";
import { ResetPasswordPage } from "@/features/auth/reset-password-page";
import { DashboardPage } from "@/features/dashboard/dashboard-page";
import { DeviceDetailPage } from "@/features/devices/device-detail-page";
import { DeviceNewPage } from "@/features/devices/device-new-page";
import { DevicesPage } from "@/features/devices/devices-page";
import { GatewaysPage } from "@/features/gateways/gateways-page";
import { SupportedDeviceDetailPage } from "@/features/hardware/supported-device-detail-page";
import { SupportedDevicesPage } from "@/features/hardware/supported-devices-page";
import { IncidentsPage } from "@/features/incidents/incidents-page";
import { LocaleRouteSync } from "@/features/marketing/locale-route-sync";
import { MarketingEquipmentPage } from "@/features/marketing/marketing-equipment-page";
import { MarketingHomePage } from "@/features/marketing/marketing-home-page";
import { NotFoundPage } from "@/features/not-found-page";
import { OrganizationsPage } from "@/features/organizations/organizations-page";
import { PlatformSettingsPage } from "@/features/platform-settings/platform-settings-page";
import { ProfilePage } from "@/features/profile/profile-page";
import { SiteDetailPage } from "@/features/sites/site-detail-page";
import { SitesPage } from "@/features/sites/sites-page";
import { OnboardingPage } from "@/features/onboarding/onboarding-page";
import { UsersPage } from "@/features/users/users-page";

/**
 * /supported-devices is not in section 9. It is a reference page rather than
 * a resource, and it exists because the honest answer to "which sensors does
 * this work with" is long enough to need somewhere to live (ADR-049). It is a
 * list with a page per device, and the id in `/supported-devices/:id` is a
 * catalogue key rather than a database row — the catalogue is static, so the
 * detail page resolves it synchronously and renders a not-found when it
 * cannot.
 *
 * `/devices/new` is declared before `/devices/:id` so it is never read as an
 * identifier.
 */
export function App() {
  return (
    <>
      <LocaleRouteSync />
      <Routes>
        <Route path="/" element={<MarketingHomePage />} />
        <Route path="/equipos" element={<MarketingEquipmentPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route element={<RequireView view="DASHBOARD" />}>
              <Route path="/dashboard" element={<DashboardPage />} />
            </Route>

            <Route element={<RequireView view="DEVICES" />}>
              <Route path="/devices" element={<DevicesPage />} />
              <Route element={<RequireRole allowed={["ADMIN", "SUPER_ADMIN"]} />}>
                <Route path="/devices/new" element={<DeviceNewPage />} />
              </Route>
              <Route path="/devices/:id" element={<DeviceDetailPage />} />
            </Route>

            <Route element={<RequireView view="SUPPORTED_DEVICES" />}>
              <Route path="/supported-devices" element={<SupportedDevicesPage />} />
              <Route path="/supported-devices/:id" element={<SupportedDeviceDetailPage />} />
            </Route>

            <Route element={<RequireView view="INCIDENTS" />}>
              <Route path="/incidents" element={<IncidentsPage />} />
            </Route>

            <Route element={<RequireView view="SITES" />}>
              <Route path="/sites" element={<SitesPage />} />
              <Route path="/sites/:id" element={<SiteDetailPage />} />
            </Route>

            <Route element={<RequireView view="GATEWAYS" />}>
              <Route path="/gateways" element={<GatewaysPage />} />
            </Route>

            <Route element={<RequireView view="ASSETS" />}>
              <Route path="/assets" element={<AssetsPage />} />
              {/* Open to every role that holds ASSETS: it reads data a plain
                user can already reach, and the person who has to produce a
                record for an inspection is not necessarily an administrator. */}
              <Route path="/assets/:id/temperature-record" element={<TemperatureRecordPage />} />
            </Route>

            <Route element={<RequireAnyView views={["LOAN_AGREEMENTS", "SERVICE_AGREEMENTS"]} />}>
              <Route path="/contracts" element={<ContractsPage />}>
                <Route index element={<ContractsIndexRedirect />} />
                <Route element={<RequireView view="LOAN_AGREEMENTS" />}>
                  <Route path="loans" element={<LoanAgreementsPage embedded />} />
                </Route>
                <Route element={<RequireView view="SERVICE_AGREEMENTS" />}>
                  <Route path="services" element={<ServiceAgreementsPage embedded />} />
                </Route>
              </Route>
            </Route>

            <Route element={<RequireRole allowed={["ADMIN", "SUPER_ADMIN"]} />}>
              <Route
                element={
                  <RequireAnyView views={["DEVICES", "LOAN_AGREEMENTS", "SERVICE_AGREEMENTS"]} />
                }
              >
                <Route path="/onboarding" element={<OnboardingPage />} />
              </Route>
            </Route>

            <Route path="/loan-agreements" element={<Navigate to="/contracts/loans" replace />} />
            <Route
              path="/service-agreements"
              element={<Navigate to="/contracts/services" replace />}
            />

            <Route element={<RequireView view="LOAN_AGREEMENTS" />}>
              <Route path="/loan-agreements/:id" element={<LoanAgreementPage />} />
            </Route>

            <Route element={<RequireView view="SERVICE_AGREEMENTS" />}>
              <Route path="/service-agreements/:id" element={<ServiceAgreementPage />} />
            </Route>

            {/* Member management is an administrative surface, and the API
              refuses a plain user on every one of its endpoints. The audit
              trail is the same: it names every member and what they did. The
              alert rules list the addresses that get told about an incident,
              which is internal contact detail for the organization. */}
            <Route element={<RequireRole allowed={["ADMIN", "SUPER_ADMIN"]} />}>
              <Route element={<RequireView view="USERS" />}>
                <Route path="/users" element={<UsersPage />} />
              </Route>
              <Route element={<RequireView view="WEBHOOK_EVENTS" />}>
                <Route path="/webhook-events" element={<WebhookEventsPage />} />
              </Route>
              <Route element={<RequireView view="AUDIT" />}>
                <Route path="/audit" element={<AuditPage />} />
              </Route>
              <Route element={<RequireView view="ALERT_RULES" />}>
                <Route path="/alert-rules" element={<AlertRulesPage />} />
              </Route>
              <Route element={<RequireView view="CONTACTS" />}>
                <Route path="/contacts" element={<ContactsPage />} />
              </Route>
            </Route>

            <Route element={<RequireRole allowed={["SUPER_ADMIN"]} />}>
              <Route path="/organizations" element={<OrganizationsPage />} />
              <Route path="/network-sync" element={<NetworkSyncPage />} />
              <Route path="/platform-settings" element={<PlatformSettingsPage />} />
            </Route>

            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}
